import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import _ from "underscore";
import {
  Container,
  Row,
  Col,
  Badge,
  Breadcrumb,
  UncontrolledDropdown,
  Card,
  CardHeader,
  BreadcrumbItem,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Table,
  Collapse,
} from "reactstrap";

import TemplateAPI from "api/api/TemplateAPI";
import PubSub from "utils/PubSub";
import PushEvents from "utils/PushEvents";
import CollapseButton from "elements/CollapseButton";
import FieldUtils from "utils/FieldUtils";
import Constants from "utils/Constants";
import StrUtils from "utils/StrUtils";
import { generateModuleSettingsTabs } from "utils/GeneralUtils";
import { newToastEvent } from "utils/ToastUtil";
import RecordNameTemplate from "components/Records/RecordNameTemplate/RecordNameTemplate";
import ManageModuleTags from "components/Modules/ManageModuleTags";
import RecordDetailLayout from "components/Records/RecordDetailLayout";
import EditModuleModal from "components/Modals/EditModuleModal";
import DeleteModuleModal from "components/Modals/DeleteModuleModal";
import AppNavbar from "components/AppNavbar/AppNavbar";
import DropdownMenuDivider from "components/Menus/DropdownMenuDivider";
import { AppConfirm } from "utils/PopupUtil";
import EditFieldDrawer from "components/Drawers/Fields/EditFieldDrawer";
import AppSpinner from "../../elements/AppSpinner";
import AppHelmet from "../../components/AppComponents/AppHelmet";

const initialModals = {
  editModule: false,
  deleteModule: false,
  editField: false,
};

const initialError = {
  renameModule: null,
  editField: null,
};

const initialSubmitting = {
  editModule: false,
  deleteModule: false,
  editField: false,
  deleteField: null,
  recordTable: false,
  detailLayout: false,
  subModules: false,
};

const initialCollapse = {
  moduleFields: true,
};

const CRMTemplateModuleDetailView = ({ urlStart }) => {
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [templateSchema, setTemplateSchema] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [modals, setModals] = useState(initialModals);
  const [selectedField, setSelectedField] = useState(null);
  const [expandedFields, setExpandedFields] = useState({});
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState("fields");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError);
  const [submitting, setSubmitting] = useState(initialSubmitting);
  const [sidebarSubmitting, setSidebarSubmitting] = useState(false);
  const [collapse, setCollapse] = useState(initialCollapse);
  const params = useParams();
  urlStart = urlStart || "templates";

  // Record Detail Layout State
  const [detailLayoutSections, setDetailLayoutSections] = useState(null);
  const [detailLayoutAdditionalSection, setDetailLayoutAdditionalSection] =
    useState(null);

  const fetchTemplate = useCallback(() => {
    setLoading(true);
    const templateID = params.templateID;
    const moduleAPIName = params.moduleAPIName;

    setTabs(
      generateModuleSettingsTabs({
        tenantID: null,
        moduleAPIName,
        isTemplate: true,
        templateID,
        isDynamic: !!currentModule?.Dynamic,
      })
    );

    TemplateAPI.getTemplateById(templateID)
      .then(
        (data) => {
          setTemplate(data.data.data.template);

          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: data.data.data.template,
            currentModuleAPIName: moduleAPIName,
          });
        },
        (err) => {
          console.error(err);
        }
      )
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const toggleModal = (name) => {
    setModals((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleSubmit = (name) => {
    setSubmitting((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleCollapse = (name) => {
    setCollapse((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleExpandField = (field) => {
    let exists = expandedFields.hasOwnProperty(field.APIName);

    if (exists) {
      setExpandedFields((prev) => ({
        ...prev,
        [field.APIName]: !prev[field.APIName],
      }));
    } else {
      setExpandedFields((prev) => ({ ...prev, [field.APIName]: true }));
    }
  };

  const handleTabChange = (val) => {
    const newTab = _.findWhere(tabs, { activeValue: val });
    const newLink = newTab.link;

    navigate(newLink, { replace: true });
    setActiveTab(val);
  };

  const handleUpdatedTemplate = (newTemplate, currModAPIName) => {
    const updatedTemplate = newTemplate;
    const updatedTemplateModules = updatedTemplate.Schema.CRM.Modules;

    const updatedCurrentModule = _.find(updatedTemplateModules, {
      APIName: currModAPIName,
    });

    setTemplate(updatedTemplate);
    setTemplateSchema(updatedTemplate.Schema);
    setCurrentModule(updatedCurrentModule);
  };

  // Template module related functions
  const renameTemplateModule = (values) => {
    toggleSubmit("editModule");
    const newName = values.Name;

    TemplateAPI.updateCRMTemplateModule(
      template.id,
      currentModule.APIName,
      newName
    )
      .then(
        (data) => {
          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: data.data.data.template,
            currentModuleAPIName: currentModule.APIName,
          });

          newToastEvent("success", "Successfully renamed template module.");
          toggleModal("editModule");
        },
        (err) => {
          console.error(err);
        }
      )
      .finally(() => toggleSubmit("editModule"));
  };

  const deleteTemplateModule = () => {
    toggleSubmit("deleteModule");

    TemplateAPI.deleteCRMTemplateModule(template.id, currentModule.APIName)
      .then(
        (data) => {
          newToastEvent(
            "success",
            `${currentModule.Name} Successfully Deleted`
          );

          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: data.data.data.template,
            currentModuleAPIName: currentModule.APIName,
          });
          navigate(`${urlStart}/global/templates/${template.id}`);
        },
        (err) => {
          console.error(err);
        }
      )
      .finally(() => toggleSubmit("deleteModule"));
  };

  // Template Module Fields related functions
  const modifyTemplateModuleField = async (
    fieldValues,
    isCreate,
    clearModal
  ) => {
    toggleSubmit("editField");

    let data;

    if (Constants.COSMOS_KEYWORDS.includes(fieldValues.apiName.toLowerCase())) {
      return newToastEvent("error", "Invalid API Name");
    }

    try {
      if (isCreate) {
        data = await FieldUtils.createTemplateModuleField(
          fieldValues,
          template.id,
          currentModule.APIName
        );
      } else {
        data = await FieldUtils.modifyTemplateModuleField(
          fieldValues,
          template.id,
          currentModule.APIName,
          selectedField.APIName
        );

        if (fieldValues.apiName !== selectedField.APIName) {
          const pageTitlePayload = FieldUtils.getUpdatedPageTitle(
            currentModule,
            selectedField.APIName,
            fieldValues.apiName,
            detailLayoutSections
          );

          if (pageTitlePayload) {
            await submitEditListLayout(
              pageTitlePayload,
              null,
              "RecordDetailView"
            );
          }

          const tablePayload = FieldUtils.getUpdatedTableLayout(
            currentModule,
            selectedField.APIName,
            fieldValues.apiName
          );

          if (tablePayload) {
            submitEditListLayout(
              { Fields: tablePayload },
              null,
              "RecordListView"
            );
          }
        }
      }
      toggleSubmit("editField");

      PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
        template: data.data.data.template,
        currentModuleAPIName: currentModule.APIName,
      });

      let message = isCreate ? "created new" : "modified";
      newToastEvent("success", `Successfully ${message} field.`);

      toggleModal("editField");
      clearModal();
      setSelectedField(null);
    } catch (err) {
      console.log(err);
      let message = "An unknown error occurred. Please try again.";

      if (err.response?.data?.message) {
        message = err.response?.data?.message;
      }

      return newToastEvent("error", message);
    }
  };

  const deleteTemplateModuleField = async (field) => {
    setSubmitting((prev) => ({ ...prev, deleteField: field.APIName }));

    try {
      const data = await TemplateAPI.deleteCRMTemplateModuleField(
        template.id,
        currentModule.APIName,
        field.APIName
      );

      PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
        template: data.data.data.template,
        currentModuleAPIName: currentModule.APIName,
      });

      newToastEvent("success", "Successfully deleted template module field");
    } catch (err) {
      console.log(err);
    } finally {
      setSubmitting((prev) => ({ ...prev, deleteField: null }));
    }
  };

  const confirmDeleteField = async (field) => {
    const confirmed = await AppConfirm({
      title: `Delete '${field.Name}' Field?`,
      text: `Are you sure you want to delete the '${field.Name}' field? This may cause inconsistencies with your records and any references associated with this module.`,
    });

    if (confirmed) deleteTemplateModuleField(field);
  };

  const getAllFieldOptions = useCallback(() => {
    let allFields = [];

    if (!currentModule) return allFields;

    allFields = allFields.concat(currentModule.Fields);

    allFields.push({
      APIName: "Created",
      Name: "Created",
      Protected: true,
      Default: true,
      FieldType: "DateTime",
    });

    allFields.push({
      APIName: "LastUpdated",
      Name: "Modified",
      Protected: true,
      Default: true,
      FieldType: "DateTime",
    });

    return allFields;
  }, [currentModule]);

  const submitEditListLayout = (payload, onClose, templateType) => {
    let submitType;

    if (templateType === Constants.TENANT_MODULE_TEMPLATES.RECORD_LIST_VIEW) {
      submitType = "recordTable";
    } else if (templateType === Constants.TENANT_MODULE_TEMPLATES.SUB_MODULES) {
      submitType = "subModules";
    } else {
      submitType = "detailLayout";
    }

    toggleSubmit(submitType);

    return TemplateAPI.modifyTemplateModuleTemplate(
      template.id,
      currentModule.APIName,
      templateType,
      payload
    )
      .then(
        (data) => {
          const currentTemplate = data.data.data.template;

          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: currentTemplate,
            currentModuleAPIName: currentModule.APIName,
          });

          if (typeof onClose === "function") onClose();

          newToastEvent(
            "success",
            `Successfully saved ${StrUtils.convertCamelCase(submitType)}`
          );
        },
        (err) => {
          newToastEvent(
            "error",
            "Could not edit Record Table Layout. Please try again"
          );
        }
      )
      .finally(() => toggleSubmit(submitType));
  };

  // --------------------------------------------- Module Tag Related Functions ---------------------------------------------
  const handleTagAdd = (newTag, setTagInput, initialTagInput) => {
    TemplateAPI.addTemplateModuleTag(template.id, currentModule.APIName, {
      Name: newTag,
    })
      .then((data) => {
        PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
          template: data.data.data.template,
          currentModuleAPIName: currentModule.APIName,
        });

        setTagInput(initialTagInput);

        newToastEvent("success", "Successfully created tag");
      })
      .catch((e) => console.error(e));
  };

  const handleTagUpdate = (tagID, newName) => {
    TemplateAPI.updateTemplateModuleTag(
      template.id,
      currentModule.APIName,
      tagID,
      {
        Name: newName,
      }
    )
      .then((data) => {
        PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
          template: data.data.data.template,
          currentModuleAPIName: currentModule.APIName,
        });

        newToastEvent("success", "Successfully updated tag");
      })
      .catch((e) => console.error(e));
  };

  const handleTagDelete = (tagID) => {
    TemplateAPI.deleteTemplateModuleTag(
      template.id,
      currentModule.APIName,
      tagID
    )
      .then((data) => {
        PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
          template: data.data.data.template,
          currentModuleAPIName: currentModule.APIName,
        });

        newToastEvent("success", "Successfully deleted tag");
      })
      .catch((e) => console.error(e));
  };

  // -------------------------------- Record Detail Layout Related Functions --------------------------------
  const openEditDetailLayout = useCallback(() => {
    let out = [];

    let module = currentModule;

    const allFields = getAllFieldOptions();

    let visitedFields = [];

    if (module?.Templates?.RecordDetailView) {
      out = module?.Templates?.RecordDetailView?.Layouts || [];
    }

    let additionalFields = _.filter(allFields, (f) => {
      return visitedFields.indexOf(f.APIName) < 0;
    });

    let additionalFieldsOut = [];

    for (let i = 0; i < additionalFields.length; i++) {
      additionalFieldsOut.push({
        Field: additionalFields[i],
        ColSpan: 1,
      });
    }

    let additionalSection = {
      Title: "Additional Information",
      Fields: additionalFieldsOut,
    };
    setDetailLayoutSections(out);
    setDetailLayoutAdditionalSection(additionalSection);
  }, [currentModule, getAllFieldOptions]);

  useEffect(() => {
    openEditDetailLayout(false);
  }, [currentModule, openEditDetailLayout]);

  const systemField = (title, subTitle, type) => {
    return (
      <tr>
        <th className="p-0"></th>
        <th scope="row" className="Name">
          <div>{title}</div>
          <small>{subTitle}</small>
        </th>
        <td>{type}</td>
        <td className="text-center"></td>
        <td className="text-right">
          <h4 className="m-0">
            <Badge
              color="light"
              style={{
                position: "relative",
                top: "-1px",
              }}
            >
              System Field
            </Badge>
          </h4>
        </td>
      </tr>
    );
  };

  let toggleShowInSidebar = async (visible = true) => {
    setSidebarSubmitting(true);

    TemplateAPI.toggleTemplateModuleSidebarVisibility(
      template.id,
      currentModule.APIName,
      visible
    )
      .then(
        (data) => {
          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: data.data.data.template,
            currentModuleAPIName: currentModule.APIName,
          });

          newToastEvent(
            "success",
            `Successfully ${visible ? "pinned" : "unpinned"} module ${
              visible ? "to" : "from"
            } sidebar`
          );
        },
        (e) => {
          console.error(e);

          newToastEvent(
            "error",
            `Could not ${visible ? "pin" : "unpin"} module ${
              visible ? "to" : "from"
            } sidebar. Try again.`
          );
        }
      )
      .finally(() => {
        setSidebarSubmitting(false);
      });
  };

  useEffect(() => {
    PubSub.subscribe(PushEvents.TEMPLATE.RELOADED, (data) => {
      handleUpdatedTemplate(data.template, data.currentModuleAPIName);
    });
  }, []);

  return (
    <>
      <AppHelmet title="Settings - CRM Modules" />
      <div className="header pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="auto" md="12">
                <h6 className="h2 mb-0">
                  {currentModule?.Name || ""}
                  {currentModule?.Protected && (
                    <Badge
                      color="light"
                      className="ml-2"
                      style={{ position: "relative", top: "-2px" }}
                    >
                      Protected
                    </Badge>
                  )}
                </h6>
                <Breadcrumb
                  className="d-none d-md-block mt-1"
                  listClassName="breadcrumb-links"
                >
                  <BreadcrumbItem>
                    <a href={`/clouds/${params.tenantID}`}>
                      <i className="mdi mdi-account-convert" />
                    </a>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <Link to={`${urlStart}/global/templates/${template?.id}`}>
                      Template Modules
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>{template?.Name || ""}</BreadcrumbItem>
                </Breadcrumb>
              </Col>
              <Col className="text-right" xs="">
                <UncontrolledDropdown>
                  <DropdownToggle caret color="" className="btn-outline-medium">
                    Actions
                  </DropdownToggle>
                  <DropdownMenu right={true}>
                    <DropdownItem
                      href="#"
                      disabled={loading || currentModule?.protected}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleModal("editModule");
                      }}
                    >
                      Rename Module
                    </DropdownItem>
                    {currentModule?.ShowInSidebar ? (
                      <DropdownItem
                        href="#"
                        disabled={loading || sidebarSubmitting}
                        onClick={(e) => {
                          e.preventDefault();

                          toggleShowInSidebar(false);
                        }}
                      >
                        Unpin From Sidebar
                      </DropdownItem>
                    ) : (
                      <DropdownItem
                        href="#"
                        disabled={loading || sidebarSubmitting}
                        onClick={(e) => {
                          e.preventDefault();

                          toggleShowInSidebar(true);
                        }}
                      >
                        Pin To Sidebar
                      </DropdownItem>
                    )}
                    <DropdownMenuDivider></DropdownMenuDivider>
                    <DropdownItem
                      href="#"
                      className="text-danger"
                      disabled={loading || currentModule?.Protected}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleModal("deleteModule");
                      }}
                    >
                      Delete Module
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </Col>
            </Row>
          </div>
          <AppNavbar
            tabs={tabs}
            activeTab={activeTab}
            onSelect={handleTabChange}
          />
        </Container>
      </div>
      <Container className="mt--6" fluid>
        <hr className="mt-0 mb-4"></hr>
        {activeTab === "fields" && (
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col xs="auto" className="pl-0 pr-0">
                  <CollapseButton
                    onClick={() => toggleCollapse("moduleFields")}
                    hidden={collapse.moduleFields}
                  />
                </Col>
                <Col>
                  <h6 className="h3 mb-0">Module Fields</h6>
                </Col>
                <Col xs="auto" className="text-right">
                  <Button
                    size="sm"
                    className="btn-outline-secondary"
                    onClick={() => toggleModal("editField")}
                  >
                    <span className="btn-inner--icon">
                      <i className="mdi mdi-plus mr-2" style={{ top: "0px" }} />
                    </span>
                    <span className="btn-inner--text">Add</span>
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={collapse.moduleFields}>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th className="p-0"></th>
                    <th>Field Label</th>
                    <th>Data Type</th>
                    <th className="text-center">Modification Protection</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <th scope="row" colSpan="4">
                        Loading...
                      </th>
                    </tr>
                  ) : (
                    <>
                      {currentModule?.Fields.length ? (
                        currentModule.Fields.map((field) => {
                          return (
                            <Fragment key={field.APIName}>
                              <tr>
                                <th className="p-0">
                                  <Button
                                    onClick={() => toggleExpandField(field)}
                                    style={{ outline: 0, boxShadow: "none" }}
                                    color=""
                                    className="btn-link text-secondary p-2 ml-2"
                                  >
                                    {expandedFields[field.APIName] ? (
                                      <i className="mdi mdi-chevron-down"></i>
                                    ) : (
                                      <i className="mdi mdi-chevron-right"></i>
                                    )}
                                  </Button>
                                </th>
                                <th scope="row" className="Name">
                                  {field.Name}
                                  {field.Required ? (
                                    <span className="text-danger">*</span>
                                  ) : null}
                                  <br />
                                  <small>{field.APIName}</small>
                                </th>

                                <td>
                                  {field.FieldType}
                                  {field.Unique ? (
                                    <Badge
                                      className="ml-2"
                                      color="secondary"
                                      style={{
                                        position: "relative",
                                        top: "-1px",
                                      }}
                                    >
                                      Unique
                                    </Badge>
                                  ) : null}
                                </td>

                                <td className="text-center">
                                  {field.Protected ? (
                                    <>
                                      <h4 className="m-0">
                                        <Badge
                                          color="success"
                                          style={{
                                            position: "relative",
                                            top: "-1px",
                                          }}
                                        >
                                          Enabled
                                        </Badge>
                                      </h4>
                                    </>
                                  ) : (
                                    <>
                                      <h4 className="m-0">
                                        <Badge
                                          color="light"
                                          style={{
                                            position: "relative",
                                            top: "-1px",
                                          }}
                                        >
                                          Disabled
                                        </Badge>
                                      </h4>
                                    </>
                                  )}
                                </td>
                                <td className="text-right">
                                  <>
                                    <Button
                                      onClick={() => {
                                        setSelectedField(field);
                                        toggleModal("editField");
                                      }}
                                      size="sm"
                                      color="secondary"
                                      outline
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="danger"
                                      outline
                                      onClick={() => confirmDeleteField(field)}
                                      disabled={submitting.deleteField}
                                    >
                                      {submitting.deleteField ===
                                      field.APIName ? (
                                        <>
                                          <AppSpinner color="danger" />
                                        </>
                                      ) : (
                                        <>Delete</>
                                      )}
                                    </Button>
                                  </>
                                </td>
                              </tr>
                              {expandedFields[field.APIName] && (
                                <tr>
                                  <td colSpan="5">
                                    <Row>
                                      <Col xs="12" md="6">
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Name:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>{field.Name}</small>
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              API Name:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>{field.APIName}</small>
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="pr-2">
                                            <small className="font-weight-bold">
                                              Description:
                                            </small>
                                          </div>
                                          <div className="border p-1">
                                            <small>
                                              {field.Description
                                                ? field.Description
                                                : "--"}
                                            </small>
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Data Type:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>{field.FieldType}</small>
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Required:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>
                                              {field.Required ? "Yes" : "No"}
                                            </small>
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Unique Values:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>
                                              {field.Unique ? "Yes" : "No"}
                                            </small>
                                          </div>
                                        </p>
                                      </Col>
                                      <Col xs="12" md="6">
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Readable Name Template:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>
                                              {field.DisplayField
                                                ? field.DisplayField
                                                : "-- Update This"}
                                            </small>
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Default Value:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>
                                              {field.DefaultValue !==
                                                undefined &&
                                              field.DefaultValue !== null
                                                ? field.DefaultValue.toString()
                                                : "--"}
                                            </small>
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Selectable Options:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            {!field.SourcesList ? "--" : ""}
                                            {field.SourcesList &&
                                            typeof field.SourcesList ==
                                              "string" ? (
                                              <small>
                                                <Badge
                                                  color="secondary"
                                                  style={{
                                                    position: "relative",
                                                    top: "-1px",
                                                  }}
                                                >
                                                  Reference
                                                </Badge>{" "}
                                                {field.SourcesList}
                                              </small>
                                            ) : (
                                              ""
                                            )}
                                            {field.SourcesList &&
                                            typeof field.SourcesList !=
                                              "string" ? (
                                              <>
                                                <Table className="table table-sm border">
                                                  <thead>
                                                    <tr>
                                                      <th>Name</th>
                                                      <th>Value</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {field.SourcesList.map(
                                                      (source, i) => (
                                                        <tr key={i}>
                                                          <td>{source.Name}</td>
                                                          <td>
                                                            {source.Value}
                                                          </td>
                                                        </tr>
                                                      )
                                                    )}
                                                  </tbody>
                                                </Table>
                                              </>
                                            ) : (
                                              ""
                                            )}
                                          </div>
                                        </p>
                                        <p className="mb-1">
                                          <div className="d-inline pr-2">
                                            <small className="font-weight-bold">
                                              Read Only:
                                            </small>
                                          </div>
                                          <div className="d-inline">
                                            <small>
                                              {field?.ReadOnly ? "Yes" : "No"}
                                            </small>
                                          </div>
                                        </p>
                                      </Col>
                                    </Row>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="3">None.</td>
                        </tr>
                      )}
                      {systemField("Created", "Created", "DateTime")}
                      {systemField("Modified", "LastUpdated", "DateTime")}
                      {systemField(
                        "Created By",
                        "CreatedBy",
                        "SingleReference"
                      )}
                      {systemField(
                        "Modified By",
                        "LastUpdatedBy",
                        "SingleReference"
                      )}
                      {systemField("Tags", "Tags", "Array")}
                    </>
                  )}
                </tbody>
              </Table>
            </Collapse>
          </Card>
        )}
        {activeTab === "recordnametemplate" && (
          <RecordNameTemplate
            currentModule={currentModule}
            sections={detailLayoutSections}
            onSave={submitEditListLayout}
            submitting={submitting.detailLayout}
          />
        )}
        {activeTab === "recordtags" && (
          <ManageModuleTags
            tags={currentModule?.Tags}
            currentModule={currentModule}
            onAdd={handleTagAdd}
            onUpdate={handleTagUpdate}
            onDelete={handleTagDelete}
          />
        )}
        {activeTab === "recorddetaillayout" && (
          <RecordDetailLayout
            layouts={detailLayoutSections}
            additionalSection={detailLayoutAdditionalSection}
            module={currentModule}
            onSave={submitEditListLayout}
            submitting={submitting.detailLayout}
          />
        )}
      </Container>
      <EditModuleModal
        open={modals.editModule}
        toggle={() => toggleModal("editModule")}
        onUpdate={renameTemplateModule}
        error={error.renameModule}
        existingName={currentModule?.Name}
        submitting={submitting.editModule}
      />
      <DeleteModuleModal
        open={modals.deleteModule}
        toggle={() => toggleModal("deleteModule")}
        onDelete={deleteTemplateModule}
        moduleName={currentModule?.Name}
        submitting={submitting.deleteModule}
        error={error.deleteModule}
      />
      <EditFieldDrawer
        showModificationProtection={true}
        defaultProtected={true}
        open={modals.editField}
        module={currentModule}
        onClose={() => {
          toggleModal("editField");
          setSelectedField(null);
        }}
        schema={templateSchema}
        onSave={modifyTemplateModuleField}
        submitting={submitting.editField}
        error={error.editField}
        field={selectedField}
      />
    </>
  );
};

export default CRMTemplateModuleDetailView;
